import type { Discrepancy, DiscrepancyCode, DiscrepancySummary } from '../../types/timerocket';
import { formatDateLong } from './format';

/**
 * The HR ↔ TimeRocket chips next to a person's name (P4). Amber = a warning HR should look at,
 * grey = information (the account records absences only). The wording never names an absence
 * type — the chips describe RECORDING and MASTER DATA, not health.
 *
 * The chip carries the short label; the tooltip carries the server's sentence with the dates.
 */
function shortDate(key: string | null): string {
    if (key === null || key.length < 10) return '';
    return `${Number(key.slice(8, 10))}.${Number(key.slice(5, 7))}.${key.slice(0, 4)}`;
}

function labelOf(d: Discrepancy): string {
    switch (d.code) {
        case 'SILENT': return d.since ? `silent since ${shortDate(d.since)}` : 'silent';
        case 'NO_TIME_RECORDING': return 'no time recording';
        case 'TARGET_STOPPED': return d.since ? `no target since ${shortDate(d.since)}` : 'no target days';
        case 'BOOKED_AFTER_LEAVE': return 'booked after leave';
        case 'NO_TIMEROCKET_ACCOUNT': return 'no TimeRocket account';
        case 'WORKLOAD_DIFFERS': return 'workload ≠ HR';
    }
}

export function DiscrepancyBadges({ items }: { items: Discrepancy[] | undefined }) {
    if (!items || items.length === 0) return null;
    return (
        <>
            {items.map(d => (
                <span
                    key={d.code}
                    className={`ml-1 inline-block whitespace-nowrap rounded-full px-1.5 py-0 align-middle text-[11px] font-normal ${
                        d.severity === 'warn' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'
                    }`}
                    title={d.since ? `${d.detail}\n(since ${formatDateLong(d.since)})` : d.detail}
                >
                    {labelOf(d)}
                </span>
            ))}
        </>
    );
}

const STRIP_LABELS: Record<DiscrepancyCode, string> = {
    SILENT: 'silent',
    TARGET_STOPPED: 'no target days',
    BOOKED_AFTER_LEAVE: 'booked after leave',
    NO_TIMEROCKET_ACCOUNT: 'no TimeRocket account',
    WORKLOAD_DIFFERS: 'workload differs',
    NO_TIME_RECORDING: 'no time recording',
};

/**
 * The count line above the table for the team and all scopes: how many people carry a warning
 * and how the chips split. Info chips are listed apart so the number of people HR must act on
 * stays honest.
 */
export function DiscrepancyStrip({ summary }: { summary: DiscrepancySummary | null }) {
    if (summary === null) return null;
    const c = summary.counts;
    const warnParts = (['SILENT', 'TARGET_STOPPED', 'BOOKED_AFTER_LEAVE', 'NO_TIMEROCKET_ACCOUNT', 'WORKLOAD_DIFFERS'] as const)
        .filter(k => c[k] > 0)
        .map(k => `${c[k]} ${STRIP_LABELS[k]}`);
    const info = c.NO_TIME_RECORDING > 0 ? `${c.NO_TIME_RECORDING} ${STRIP_LABELS.NO_TIME_RECORDING}` : null;
    const hint = `Silent after ${summary.thresholds.silentAfterWorkdays} workdays without a booking (${summary.thresholds.silentNoTimeRecordingWorkdays} for accounts that record absences only); workload tolerance ${summary.thresholds.workloadTolerancePts} points. Thresholds in TimeRocketConfig.`;
    if (summary.people === 0 && info === null) {
        return (
            <div className="rounded border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs text-gray-600" title={hint}>
                HR ↔ TimeRocket: no discrepancies in this scope
            </div>
        );
    }
    return (
        <div
            className={`rounded border px-3 py-1.5 text-xs ${summary.people > 0 ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-gray-200 bg-gray-50 text-gray-600'}`}
            title={hint}
        >
            <span className="font-medium">HR ↔ TimeRocket:</span>{' '}
            {summary.people > 0
                ? `${summary.people} ${summary.people === 1 ? 'person' : 'people'} with a discrepancy — ${warnParts.join(' · ')}`
                : 'no discrepancies in this scope'}
            {info && <span className="text-gray-600">{summary.people > 0 ? ' · ' : ' — '}info: {info}</span>}
        </div>
    );
}
